import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import axios from 'axios';
import { 
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  LinkIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { FaGithub } from 'react-icons/fa';
import { SiNotion } from 'react-icons/si';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import gmailSvg from '../../../assets/gmail.svg';

// Custom Google Drive icon using the provided SVG
const GoogleDriveIcon = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    x="0px" 
    y="0px" 
    width="58" 
    height="58" 
    viewBox="4 6 40 36"
    {...props}
  >
    <path fill="#1e88e5" d="M38.59,39c-0.535,0.93-0.298,1.68-1.195,2.197C36.498,41.715,35.465,42,34.39,42H13.61 c-1.074,0-2.106-0.285-3.004-0.802C9.708,40.681,9.945,39.93,9.41,39l7.67-9h13.84L38.59,39z"></path>
    <path fill="#fbc02d" d="M27.463,6.999c1.073-0.002,2.104-0.716,3.001-0.198c0.897,0.519,1.66,1.27,2.197,2.201l10.39,17.996 c0.537,0.93,0.807,1.967,0.808,3.002c0.001,1.037-1.267,2.073-1.806,3.001l-11.127-3.005l-6.924-11.993L27.463,6.999z"></path>
    <path fill="#e53935" d="M43.86,30c0,1.04-0.27,2.07-0.81,3l-3.67,6.35c-0.53,0.78-1.21,1.4-1.99,1.85L30.92,30H43.86z"></path>
    <path fill="#4caf50" d="M5.947,33.001c-0.538-0.928-1.806-1.964-1.806-3c0.001-1.036,0.27-2.073,0.808-3.004l10.39-17.996 c0.537-0.93,1.3-1.682,2.196-2.2c0.897-0.519,1.929,0.195,3.002,0.197l3.459,11.009l-6.922,11.989L5.947,33.001z"></path>
    <path fill="#1565c0" d="M17.08,30l-6.47,11.2c-0.78-0.45-1.46-1.07-1.99-1.85L4.95,33c-0.54-0.93-0.81-1.96-0.81-3H17.08z"></path>
    <path fill="#2e7d32" d="M30.46,6.8L24,18L17.53,6.8c0.78-0.45,1.66-0.73,2.6-0.79L27.46,6C28.54,6,29.57,6.28,30.46,6.8z"></path>
  </svg>
);

const IntegrationsSection = ({ user }) => {
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);

  // HubSpot integration state
  const [hubspotIntegration, setHubspotIntegration] = useState(null);
  const [loadingHubSpot, setLoadingHubSpot] = useState(false);
  const [showHubSpotSetup, setShowHubSpotSetup] = useState(false);

  // Google Drive integration state
  const [googleDriveIntegration, setGoogleDriveIntegration] = useState(null);
  const [loadingGoogleDrive, setLoadingGoogleDrive] = useState(false);

  // GitHub integration state
  const [githubIntegration, setGithubIntegration] = useState(null);
  const [loadingGitHub, setLoadingGitHub] = useState(false);

  // Notion integration state
  const [notionIntegration, setNotionIntegration] = useState(null);
  const [loadingNotion, setLoadingNotion] = useState(false);

  // Gmail integration state
  const [gmailIntegration, setGmailIntegration] = useState(null);
  const [loadingGmail, setLoadingGmail] = useState(false);

  // HubSpot OAuth integration state (direct API: hubspot_get_contacts, etc.)
  const [hubspotMcpIntegration, setHubspotMcpIntegration] = useState(null);
  const [loadingHubSpotMcp, setLoadingHubSpotMcp] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchHubSpotIntegration(),
        fetchGoogleDriveIntegration(),
        fetchGitHubIntegration(),
        fetchNotionIntegration(),
        fetchGmailIntegration(),
        fetchHubspotMcpIntegration()
      ]);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHubSpotIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/integrations/hubspot`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setHubspotIntegration(response.data.integration);
      }
    } catch (error) {
      // It's okay if this fails - user might not have HubSpot connected yet
      console.log('No HubSpot integration found');
    }
  };

  const fetchGoogleDriveIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.connected) {
        setGoogleDriveIntegration(response.data);
      }
    } catch (error) {
      console.log('No Google Drive integration found');
    }
  };

  const fetchGitHubIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/github/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.connected) {
        setGithubIntegration(response.data);
      } else {
        setGithubIntegration(null);
      }
    } catch (error) {
      console.log('No GitHub integration found');
      setGithubIntegration(null);
    }
  };

  const fetchNotionIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/notion/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.connected) {
        setNotionIntegration(response.data);
      } else {
        setNotionIntegration(null);
      }
    } catch (error) {
      console.log('No Notion integration found');
      setNotionIntegration(null);
    }
  };

  const fetchGmailIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/status?connector=gmail`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.connected) {
        setGmailIntegration(response.data);
      } else {
        setGmailIntegration(null);
      }
    } catch (error) {
      console.log('No Gmail integration found');
      setGmailIntegration(null);
    }
  };

  const fetchHubspotMcpIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const response = await axios.get(`${apiUrl}/hubspot-oauth/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.connected) {
        setHubspotMcpIntegration(response.data.integration);
        console.log('HubSpot OAuth Integration status:', response.data.integration);
      } else {
        setHubspotMcpIntegration(null);
      }
    } catch (error) {
      console.log('No HubSpot OAuth integration found');
      setHubspotMcpIntegration(null);
    }
  };

  // HubSpot handlers (legacy - keeping for existing HubSpot integration)

  const disconnectHubSpot = async () => {
    if (!window.confirm('Are you sure you want to disconnect HubSpot? This will remove access to your HubSpot data.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/integrations/hubspot`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('HubSpot disconnected successfully');
        setHubspotIntegration(null);
      }
    } catch (error) {
      console.error('Error disconnecting HubSpot:', error);
      toast.error('Failed to disconnect HubSpot');
    }
  };

  const testHubSpotConnection = async () => {
    try {
      setLoadingHubSpot(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/integrations/hubspot/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`HubSpot connection successful! Found ${response.data.contactsCount} contacts`);
      }
    } catch (error) {
      console.error('Error testing HubSpot connection:', error);
      toast.error('HubSpot connection test failed');
    } finally {
      setLoadingHubSpot(false);
    }
  };

  // Google Drive handlers
  // const connectGoogleDrive = async () => {
  //   toast.info('Google Drive integration coming soon!');
  // };

  const disconnectGoogleDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive? This will remove access to your Google Drive documents.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google-drive/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Google Drive disconnected successfully');
        setGoogleDriveIntegration(null);
      }
    } catch (error) {
      console.error('Error disconnecting Google Drive:', error);
      toast.error('Failed to disconnect Google Drive');
    }
  };

  const testGoogleDriveConnection = async () => {
    try {
      setLoadingGoogleDrive(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/recent-docs`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`Google Drive connection successful! Found ${response.data.documents.length} recent documents`);
      }
    } catch (error) {
      console.error('Error testing Google Drive connection:', error);
      toast.error('Google Drive connection test failed');
    } finally {
      setLoadingGoogleDrive(false);
    }
  };

  // Gmail handlers
  const connectGmail = async () => {
    try {
      setLoadingGmail(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/connect?connector=gmail`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      } else {
        toast.error('Failed to start Gmail connection');
      }
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      toast.error('Failed to connect Gmail');
    } finally {
      setLoadingGmail(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? This will remove access to your Gmail.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google-drive/disconnect`, { connector: 'gmail' }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Gmail disconnected successfully');
        setGmailIntegration(null);
      }
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
      toast.error('Failed to disconnect Gmail');
    }
  };

  const connectGitHub = async () => {
    try {
      setLoadingGitHub(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/github/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.authUrl) {
        const popup = window.open(
          response.data.authUrl,
          'github-oauth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        const handleOAuthMessage = (event) => {
          if (event.origin !== window.location.origin || event.data?.type !== 'oauth-callback') return;
          if (event.data.success) {
            toast.success('GitHub connected successfully!');
          } else if (event.data.error) {
            toast.error('GitHub connection failed. Please try again.');
          }
          window.removeEventListener('message', handleOAuthMessage);
        };
        window.addEventListener('message', handleOAuthMessage);

        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleOAuthMessage);
            fetchGitHubIntegration();
            setLoadingGitHub(false);
          }
        }, 500);

        setTimeout(() => {
          if (!popup.closed) {
            popup.close();
            clearInterval(checkClosed);
            window.removeEventListener('message', handleOAuthMessage);
            setLoadingGitHub(false);
            toast.error('Authorization timed out. Please try again.');
          }
        }, 5 * 60 * 1000);
      } else {
        toast.error('Failed to start GitHub connection');
        setLoadingGitHub(false);
      }
    } catch (error) {
      console.error('GitHub connect error:', error);
      toast.error('Failed to connect GitHub');
      setLoadingGitHub(false);
    }
  };

  const disconnectGitHub = async () => {
    if (!window.confirm('Are you sure you want to disconnect GitHub? This will remove access to your repositories.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/github/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('GitHub disconnected successfully');
        setGithubIntegration(null);
      }
    } catch (error) {
      console.error('Error disconnecting GitHub:', error);
      toast.error('Failed to disconnect GitHub');
    }
  };

  const testGitHubConnection = async () => {
    try {
      setLoadingGitHub(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/github/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.connected) {
        toast.success(`GitHub connected as @${response.data.username || 'user'}`);
      } else {
        toast.error('GitHub is not connected');
      }
    } catch (error) {
      toast.error('Failed to test GitHub connection');
    } finally {
      setLoadingGitHub(false);
    }
  };

  const connectNotion = async () => {
    try {
      setLoadingNotion(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/notion/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      } else {
        toast.error('Failed to start Notion connection');
        setLoadingNotion(false);
      }
    } catch (error) {
      console.error('Error connecting Notion:', error);
      toast.error('Failed to connect Notion');
      setLoadingNotion(false);
    }
  };

  const disconnectNotion = async () => {
    if (!window.confirm('Are you sure you want to disconnect Notion? This will remove access to your Notion workspace.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/notion/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Notion disconnected successfully');
        setNotionIntegration(null);
      }
    } catch (error) {
      console.error('Error disconnecting Notion:', error);
      toast.error('Failed to disconnect Notion');
    }
  };

  const testNotionConnection = async () => {
    try {
      setLoadingNotion(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/notion/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.connected) {
        toast.success(`Notion connected${response.data.workspaceName ? ` to ${response.data.workspaceName}` : ''}`);
      } else {
        toast.error('Notion is not connected');
      }
    } catch (error) {
      toast.error('Failed to test Notion connection');
    } finally {
      setLoadingNotion(false);
    }
  };

  const renderHubSpotSetupModal = () => {
    if (!showHubSpotSetup) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-[#111111] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Connect HubSpot Account
              </h3>
              <button
                onClick={() => setShowHubSpotSetup(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <div className="p-2">
                  <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 489 511.8" className="w-10 h-10">
                    <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z"/>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">HubSpot CRM Integration</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Connect your HubSpot account to access contacts, deals, companies, and more through Midus.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">What you'll get access to:</h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    Access to all your HubSpot contacts, companies, and deals
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    Create and update CRM records through chat
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    Generate insights and reports from your data
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    Add tasks, notes, and manage engagements
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2" />
                  <div>
                    <h5 className="font-medium text-yellow-800 dark:text-yellow-200">Important Note</h5>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      You'll be redirected to HubSpot to authorize this application. Make sure you have admin access to your HubSpot account.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowHubSpotSetup(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-[#1A1A1A] dark:text-gray-300 dark:border-gray-600 dark:hover:bg-[#2A2A2A]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowHubSpotSetup(false);
                    initiateHubSpotOAuth();
                  }}
                  disabled={loadingHubSpot}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#FF7A59] rounded-lg hover:bg-[#FF6B47] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingHubSpot ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-2 inline" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4 mr-2 inline" />
                      Connect to HubSpot
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // HubSpot OAuth handlers
  const initiateHubSpotOAuth = async () => {
    try {
      setLoadingHubSpotMcp(true);
      const token = localStorage.getItem('token');
      
      // Get OAuth authorization URL - use localhost for OAuth (HubSpot requirement)
      const response = await axios.get(`http://localhost:8081/api/hubspot-oauth/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Open OAuth popup (like Claude does)
        const popup = window.open(
          response.data.authUrl,
          'hubspot-oauth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Monitor popup for completion
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            
            // Check for success/error parameters
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('hubspot_success')) {
              toast.success('HubSpot connected successfully!');
              // Clear URL parameters
              window.history.replaceState({}, document.title, window.location.pathname);
            } else if (urlParams.get('hubspot_error')) {
              toast.error('HubSpot connection failed. Please try again.');
              // Clear URL parameters
              window.history.replaceState({}, document.title, window.location.pathname);
            }
            
            // Refresh integration status
            fetchHubspotMcpIntegration();
            setLoadingHubSpotMcp(false);
          }
        }, 1000);

        // Timeout after 5 minutes
        setTimeout(() => {
          if (!popup.closed) {
            popup.close();
            clearInterval(checkClosed);
            setLoadingHubSpotMcp(false);
            toast.error('Authorization timed out. Please try again.');
          }
        }, 5 * 60 * 1000);

      } else {
        toast.error('Failed to initiate HubSpot authorization');
        setLoadingHubSpotMcp(false);
      }
    } catch (error) {
      console.error('OAuth initiation error:', error);
      toast.error('Failed to connect to HubSpot');
      setLoadingHubSpotMcp(false);
    }
  };

  const testHubSpotMcpConnection = async () => {
    setLoadingHubSpotMcp(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const response = await axios.post(`${apiUrl}/hubspot-oauth/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('HubSpot connection test successful!');
        fetchHubspotMcpIntegration();
      } else {
        toast.error(response.data.error || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Failed to test HubSpot connection');
    } finally {
      setLoadingHubSpotMcp(false);
    }
  };

  const disconnectHubSpotMcp = async () => {
    setLoadingHubSpotMcp(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const response = await axios.post(`${apiUrl}/hubspot-oauth/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setHubspotMcpIntegration(null);
        toast.success('HubSpot disconnected successfully');
      } else {
        toast.error('Failed to disconnect HubSpot');
      }
    } catch (error) {
      toast.error('Failed to disconnect HubSpot');
    } finally {
      setLoadingHubSpotMcp(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div>
          <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
            <Skeleton height={28} width={200} className="mb-2" />
            <Skeleton height={16} width={400} />
          </SkeletonTheme>
        </div>

        {/* Integration cards skeleton */}
        <div className="grid grid-cols-2 gap-6">
          <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-gray-300 dark:border-[#282828]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 flex items-center justify-center">
                      <Skeleton circle height={24} width={24} />
                    </div>
                    <div>
                      <Skeleton height={20} width={100} />
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Skeleton height={32} width={80} />
                  </div>
                </div>
              </div>
            ))}
          </SkeletonTheme>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Integrations</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Connect your favorite tools and services to enhance your workflow
        </p>
      </div>

      {/* Integration Grid - 2 columns */}
      <div className="grid grid-cols-2 gap-6">
                  {/* Google Drive Integration */}
          <div className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-blue-500 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-2 flex items-center justify-center">
                  <GoogleDriveIcon className="w-6 h-6" />
                </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Google Drive</h3>
              </div>
            </div>
            
            {/* Button aligned to the right */}
            <div className="flex items-center space-x-3">
              {googleDriveIntegration ? (
                <>
                  <button
                    onClick={testGoogleDriveConnection}
                    disabled={loadingGoogleDrive}
                    className="px-3 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {loadingGoogleDrive ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                  <button
                    onClick={disconnectGoogleDrive}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  disabled={true}
                  className="px-4 py-2 bg-gray-400 text-gray-600 rounded-xl text-sm cursor-not-allowed font-medium transition-colors opacity-50"
                >
                  Coming Soon
                </button>
              )}
            </div>
          </div>

        {/* Gmail Integration */}
        <div className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-red-500 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)] transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 flex items-center justify-center">
                <img src={gmailSvg} alt="Gmail" className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gmail</h3>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {gmailIntegration ? (
                <>
                  <button
                    onClick={disconnectGmail}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connectGmail}
                  disabled={loadingGmail}
                  className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {loadingGmail ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-2 inline" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* GitHub Integration */}
        <div className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-gray-600 hover:shadow-[0_0_20px_rgba(107,114,128,0.3)] transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 flex items-center justify-center">
                <FaGithub className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">GitHub</h3>
              </div>
            </div>
            
            {/* Button aligned to the right */}
            <div className="flex items-center space-x-3">
              {githubIntegration ? (
                <>
                  <button
                    onClick={testGitHubConnection}
                    disabled={loadingGitHub}
                    className="px-3 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {loadingGitHub ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                  <button
                    onClick={disconnectGitHub}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connectGitHub}
                  disabled={loadingGitHub}
                  className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-xl text-sm hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {loadingGitHub ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-2 inline" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
              )}
            </div>
          </div>
          

        </div>

        {/* Notion Integration */}
        <div className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-gray-600 hover:shadow-[0_0_20px_rgba(107,114,128,0.3)] transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 flex items-center justify-center">
                <SiNotion className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notion</h3>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {notionIntegration ? (
                <>
                  <button
                    onClick={testNotionConnection}
                    disabled={loadingNotion}
                    className="px-3 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {loadingNotion ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                  <button
                    onClick={disconnectNotion}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connectNotion}
                  disabled={loadingNotion}
                  className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-xl text-sm hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {loadingNotion ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-2 inline" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* HubSpot Integration - Commented out in favor of MCP version */}
        {/* <div className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-[#FF7A59] hover:shadow-[0_0_20px_rgba(255,122,89,0.3)] transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 489 511.8" className="w-6 h-6">
                  <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">HubSpot</h3>
              </div>
            </div>
            
            {/* Button aligned to the right */}
            {/* <div className="flex items-center space-x-3">
              {hubspotIntegration ? (
                <>
                  <button
                    onClick={testHubSpotConnection}
                    disabled={loadingHubSpot}
                    className="px-3 py-2 bg-[#FF7A59] text-white rounded-xl hover:bg-[#FF6B47] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {loadingHubSpot ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                  <button
                    onClick={disconnectHubSpot}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowHubSpotSetup(true)}
                  disabled={loadingHubSpot}
                  className="px-4 py-2 bg-[#FF7A59] text-white rounded-xl text-sm hover:bg-[#FF6B47] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {loadingHubSpot ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-2 inline" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
              )}
            </div>
          </div>
          

        </div> */}

        {/* HubSpot MCP Integration - New Addition */}
        <div className="bg-white dark:bg-[#111111] rounded-3xl p-4 border border-[#FF7A59] hover:shadow-[0_0_20px_rgba(255,122,89,0.3)] transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 489 511.8" className="w-6 h-6">
                  <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  HubSpot
                
                </h3>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {hubspotMcpIntegration ? (
                <>
                  <button
                    onClick={testHubSpotMcpConnection}
                    disabled={loadingHubSpotMcp}
                    className="px-3 py-2 bg-[#FF7A59] text-white rounded-xl hover:bg-[#FF6B47] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {loadingHubSpotMcp ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                  <button
                    onClick={disconnectHubSpotMcp}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={initiateHubSpotOAuth}
                  disabled={loadingHubSpotMcp}
                  className="px-4 py-2 bg-[#FF7A59] text-white rounded-xl text-sm hover:bg-[#FF6B47] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {loadingHubSpotMcp ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-2 inline" />
                      Connecting...
                    </>
                  ) : (
                    'Connect with HubSpot'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {renderHubSpotSetupModal()}
      {/* OAuth flow replaces the complex setup wizard */}
    </div>
  );
};

export default IntegrationsSection; 